import { useState, useCallback } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core'
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable'
import * as Popover from '@radix-ui/react-popover'
import { ChevronDown, Plus, Lock } from 'lucide-react'

import { cn } from '@/lib/utils'
import { api } from '@/lib/api'
import { useWorkspaceStore } from '@/stores/workspace.store'
import { useUpgradeStore } from '@/stores/upgrade.store'
import type { ApiResponse } from '@/types/api'
import type { Application, ApplicationStatus, Cycle, CycleSeason } from '@/types/models'

import ApplicationCard from '@/components/applications/ApplicationCard'
import ApplicationDrawer from '@/components/applications/ApplicationDrawer'
import AddApplicationModal from '@/components/applications/AddApplicationModal'

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const COLUMNS: { status: ApplicationStatus; label: string }[] = [
  { status: 'RESEARCHING', label: 'Researching' },
  { status: 'PREPARING', label: 'Preparing' },
  { status: 'SUBMITTED', label: 'Submitted' },
  { status: 'DECISION', label: 'Decision' },
]

const SEASONS: CycleSeason[] = ['FALL', 'SPRING', 'SUMMER', 'WINTER']

// ---------------------------------------------------------------------------
// CreateCyclePopover
// ---------------------------------------------------------------------------

interface CreateCyclePopoverProps {
  workspaceId: string
  onCreated: (cycle: Cycle) => void
}

function CreateCyclePopover({ workspaceId, onCreated }: CreateCyclePopoverProps) {
  const queryClient = useQueryClient()
  const [open, setOpen] = useState(false)
  const [name, setName] = useState('')
  const [season, setSeason] = useState<CycleSeason>('FALL')
  const [year, setYear] = useState(new Date().getFullYear())
  const [error, setError] = useState('')

  const createMutation = useMutation({
    mutationFn: (data: { name: string; season: CycleSeason; year: number }) =>
      api.post<ApiResponse<Cycle>>('/v1/cycles', data),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['cycles', workspaceId] })
      if (res.data) {
        onCreated(res.data)
      }
      setOpen(false)
      setName('')
      setError('')
    },
    onError: (err: Error) => setError(err.message),
  })

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!name.trim()) {
      setError('Name is required.')
      return
    }
    setError('')
    createMutation.mutate({ name: name.trim(), season, year })
  }

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <button
          type="button"
          className="w-full rounded-md px-3 py-2 text-left text-sm text-brand-blue hover:bg-accent"
        >
          + Create Cycle
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          side="bottom"
          align="start"
          sideOffset={4}
          className="z-50 w-72 rounded-xl border bg-popover p-4 shadow-lg"
        >
          <form onSubmit={handleSubmit} className="space-y-3">
            <p className="text-sm font-semibold text-foreground">New Cycle</p>
            <input
              type="text"
              placeholder="Cycle name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoFocus
              className="w-full rounded border bg-background px-3 py-1.5 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            />
            <div className="flex gap-2">
              <select
                value={season}
                onChange={(e) => setSeason(e.target.value as CycleSeason)}
                className="flex-1 rounded border bg-background px-2 py-1.5 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                {SEASONS.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
              <input
                type="number"
                value={year}
                onChange={(e) => setYear(parseInt(e.target.value, 10))}
                min={2020}
                max={2040}
                className="w-24 rounded border bg-background px-2 py-1.5 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              />
            </div>
            {error && <p className="text-xs text-destructive">{error}</p>}
            <button
              type="submit"
              disabled={createMutation.isPending}
              className="w-full rounded-md bg-brand-blue px-3 py-1.5 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-60"
            >
              {createMutation.isPending ? 'Creating...' : 'Create'}
            </button>
          </form>
          <Popover.Arrow className="fill-border" />
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  )
}

// ---------------------------------------------------------------------------
// KanbanColumn
// ---------------------------------------------------------------------------

interface KanbanColumnProps {
  status: ApplicationStatus
  label: string
  applications: Application[]
  onCardClick: (app: Application) => void
}

function KanbanColumn({ status, label, applications, onCardClick }: KanbanColumnProps) {
  const { setNodeRef, isOver } = useDroppable({ id: status })

  return (
    <div className="flex flex-col min-h-0 flex-1">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-foreground">{label}</h3>
        <span className="rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">
          {applications.length}
        </span>
      </div>
      <div
        ref={setNodeRef}
        className={cn(
          'flex-1 rounded-lg p-2 min-h-[200px] transition-colors',
          isOver ? 'bg-brand-blue/10' : 'bg-muted/30',
        )}
      >
        <SortableContext
          items={applications.map((a) => a.id)}
          strategy={verticalListSortingStrategy}
          id={status}
        >
          <div className="space-y-2">
            {applications.map((app) => (
              <ApplicationCard
                key={app.id}
                application={app}
                onClick={() => onCardClick(app)}
              />
            ))}
          </div>
        </SortableContext>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// ApplicationsPage
// ---------------------------------------------------------------------------

/**
 * ApplicationsPage — Kanban board for tracking MBA applications.
 *
 * Features:
 * - Cycle selector bar with "Create Cycle" popover
 * - 4-column drag-and-drop Kanban (RESEARCHING → PREPARING → SUBMITTED → DECISION)
 * - Optimistic status updates on drag-end
 * - ApplicationDrawer for inline editing
 * - AddApplicationModal for creating new applications
 * - Freemium limit enforcement (lock button when limit reached)
 */
export default function ApplicationsPage() {
  const queryClient = useQueryClient()
  const { currentWorkspaceId, selectedCycleId, setSelectedCycleId } = useWorkspaceStore()
  const openUpgrade = useUpgradeStore((s) => s.open)

  const [drawerApp, setDrawerApp] = useState<Application | null>(null)
  const [addModalOpen, setAddModalOpen] = useState(false)
  const [cycleDropdownOpen, setCycleDropdownOpen] = useState(false)
  const [activeDragApp, setActiveDragApp] = useState<Application | null>(null)

  // Sensors — require 5px movement to distinguish click from drag
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
  )

  // Fetch cycles
  const { data: cyclesData } = useQuery<ApiResponse<Cycle[]>>({
    queryKey: ['cycles', currentWorkspaceId],
    queryFn: () => api.get<ApiResponse<Cycle[]>>('/v1/cycles'),
    enabled: !!currentWorkspaceId,
  })
  const cycles = cyclesData?.data ?? []

  // Auto-select first cycle if none selected
  const activeCycleId = selectedCycleId ?? cycles[0]?.id ?? null

  // Fetch applications for active cycle
  const { data: appsData, isLoading } = useQuery<ApiResponse<Application[]>>({
    queryKey: ['applications', currentWorkspaceId, activeCycleId],
    queryFn: () =>
      api.get<ApiResponse<Application[]>>(
        `/v1/applications?cycleId=${activeCycleId}`,
      ),
    enabled: !!currentWorkspaceId && !!activeCycleId,
  })
  const applications = appsData?.data ?? []

  // Billing status for limit enforcement
  const { data: billingData } = useQuery({
    queryKey: ['billing-status', currentWorkspaceId],
    queryFn: () => api.get<ApiResponse<{ tier: string; limit: number; applicationsUsed: number }>>('/v1/billing/status'),
    enabled: !!currentWorkspaceId,
  })
  const limit = billingData?.data?.limit ?? Infinity
  const used = applications.length
  const atLimit = used >= limit

  // Optimistic status update mutation
  const patchStatusMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: ApplicationStatus }) =>
      api.patch(`/v1/applications/${id}`, { status }),
    onMutate: async ({ id, status }) => {
      await queryClient.cancelQueries({
        queryKey: ['applications', currentWorkspaceId, activeCycleId],
      })
      const previous = queryClient.getQueryData<ApiResponse<Application[]>>([
        'applications',
        currentWorkspaceId,
        activeCycleId,
      ])
      queryClient.setQueryData<ApiResponse<Application[]>>(
        ['applications', currentWorkspaceId, activeCycleId],
        (old) => {
          if (!old?.data) return old
          return {
            ...old,
            data: old.data.map((a) => (a.id === id ? { ...a, status } : a)),
          }
        },
      )
      return { previous }
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(
          ['applications', currentWorkspaceId, activeCycleId],
          context.previous,
        )
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: ['applications', currentWorkspaceId, activeCycleId],
      })
    },
  })

  const handleDragStart = useCallback(
    (event: DragStartEvent) => {
      const app = applications.find((a) => a.id === event.active.id)
      setActiveDragApp(app ?? null)
    },
    [applications],
  )

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      setActiveDragApp(null)
      const { active, over } = event
      if (!over) return

      // Determine the target column from the over element's id or its container
      const overId = over.id as string
      const targetStatus = COLUMNS.find((c) => c.status === overId)?.status
        ?? applications.find((a) => a.id === overId)?.status

      const draggedApp = applications.find((a) => a.id === active.id)

      if (!draggedApp || !targetStatus || draggedApp.status === targetStatus) return

      patchStatusMutation.mutate({ id: draggedApp.id, status: targetStatus })
    },
    [applications, patchStatusMutation],
  )

  const activeCycle = cycles.find((c) => c.id === activeCycleId)

  function handleAddApplicationClick() {
    if (atLimit) {
      openUpgrade()
    } else {
      setAddModalOpen(true)
    }
  }

  return (
    <div className="flex h-full flex-col">
      {/* Cycle selector bar */}
      <div className="flex items-center justify-between border-b bg-background px-5 py-3">
        {/* Cycle dropdown */}
        <div className="relative">
          <button
            type="button"
            onClick={() => setCycleDropdownOpen((o) => !o)}
            className="flex items-center gap-2 rounded-md border bg-card px-3 py-1.5 text-sm font-medium hover:bg-accent focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            aria-haspopup="listbox"
            aria-expanded={cycleDropdownOpen}
          >
            <span>
              {activeCycle
                ? `${activeCycle.name} (${activeCycle.season} ${activeCycle.year})`
                : 'Select Cycle'}
            </span>
            <ChevronDown className="h-4 w-4 text-muted-foreground" />
          </button>

          {cycleDropdownOpen && (
            <div className="absolute left-0 top-full z-20 mt-1 w-64 rounded-lg border bg-popover shadow-lg">
              {cycles.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  role="option"
                  aria-selected={c.id === activeCycleId}
                  onClick={() => {
                    setSelectedCycleId(c.id)
                    setCycleDropdownOpen(false)
                  }}
                  className={cn(
                    'flex w-full items-center px-3 py-2 text-sm hover:bg-accent',
                    c.id === activeCycleId && 'font-medium text-brand-blue',
                  )}
                >
                  {c.name} ({c.season} {c.year})
                </button>
              ))}
              <div className="border-t">
                <CreateCyclePopover
                  workspaceId={currentWorkspaceId ?? ''}
                  onCreated={(cycle) => {
                    setSelectedCycleId(cycle.id)
                    setCycleDropdownOpen(false)
                  }}
                />
              </div>
            </div>
          )}
        </div>

        {/* Right side: usage + add button */}
        <div className="flex items-center gap-3">
          {limit !== Infinity && (
            <span className="text-xs text-muted-foreground">
              {used} / {limit} applications used
            </span>
          )}
          <button
            type="button"
            onClick={handleAddApplicationClick}
            title={atLimit ? 'Upgrade to add more' : 'Add Application'}
            className={cn(
              'flex items-center gap-2 rounded-md px-3 py-1.5 text-sm font-semibold text-white transition-opacity focus:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              atLimit ? 'bg-muted text-muted-foreground cursor-default' : 'bg-brand-blue hover:opacity-90',
            )}
          >
            {atLimit ? <Lock className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
            Add Application
          </button>
        </div>
      </div>

      {/* Kanban board */}
      <div className="flex-1 overflow-auto p-5">
        {isLoading && (
          <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
            Loading applications...
          </div>
        )}

        {!isLoading && !activeCycleId && cycles.length === 0 && (
          <div className="flex h-full flex-col items-center justify-center gap-3">
            <p className="text-muted-foreground">No cycles yet.</p>
            <p className="text-sm text-muted-foreground">
              Create a cycle from the dropdown above to get started.
            </p>
          </div>
        )}

        {!isLoading && activeCycleId && (
          <DndContext
            sensors={sensors}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
          >
            <div className="flex gap-4 h-full min-w-0">
              {COLUMNS.map(({ status, label }) => {
                const colApps = applications.filter((a) => a.status === status)
                return (
                  <KanbanColumn
                    key={status}
                    status={status}
                    label={label}
                    applications={colApps}
                    onCardClick={(app) => setDrawerApp(app)}
                  />
                )
              })}
            </div>

            <DragOverlay>
              {activeDragApp && (
                <div className="rotate-2 opacity-80">
                  <ApplicationCard
                    application={activeDragApp}
                    onClick={() => {}}
                  />
                </div>
              )}
            </DragOverlay>
          </DndContext>
        )}
      </div>

      {/* Application Drawer */}
      <ApplicationDrawer
        application={drawerApp}
        workspaceId={currentWorkspaceId ?? ''}
        cycleId={activeCycleId}
        onClose={() => setDrawerApp(null)}
      />

      {/* Add Application Modal */}
      <AddApplicationModal
        isOpen={addModalOpen}
        onClose={() => setAddModalOpen(false)}
        workspaceId={currentWorkspaceId ?? ''}
      />
    </div>
  )
}
